import { round } from 'lodash'
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core'
import React, { useState } from 'react'
import { Plus } from 'react-feather'
import { useTranslation } from 'react-i18next'
import { useReactiveVar } from '@apollo/client'
import { projectVar } from '../../../apollo/reactive-vars'
import { useSchedules } from '../../../schedule/use-schedules'
import {
  BuildingEnergyModelFragment,
  ScheduleFragment,
} from '../../../generated/graphql'
import { LoadingDialog } from '../../common/LoadingDialog'
import { ScheduleDialog } from './ScheduleDialog'
import {
  defaultValues,
  FormValues,
  mapFormValuesToInput,
  mapInputToFormValues,
} from './form-values'
import { formatDate } from '../../common/util/date-util'
import { theme } from '../../../theme'
import { calculateHourSerieFactorsSum } from '../../../schedule/hour-series-factors-calculator'

type Props = {
  buildingEnergyModel: BuildingEnergyModelFragment
}

export function ScheduleList({
  buildingEnergyModel,
}: Props): React.ReactElement {
  const [isCreatingSchedule, setIsCreatingSchedule] = useState(false)
  const [editingSchedule, setEditingSchedule] = useState<
    ScheduleFragment | undefined
  >()

  const { t } = useTranslation()

  const {
    createSchedule,
    isCreateLoading,
    updateSchedule,
    isUpdateLoading,
    deleteSchedule,
    isDeleteLoading,
  } = useSchedules()

  const project = useReactiveVar(projectVar)

  const { schedules } = buildingEnergyModel

  const onDialogClose = () => {
    setIsCreatingSchedule(false)
    setEditingSchedule(undefined)
  }

  const onCreateStart = () => setIsCreatingSchedule(true)

  const onCreateSubmit = async (values: FormValues) => {
    const input = mapFormValuesToInput(values)
    await createSchedule(buildingEnergyModel, input)
    setIsCreatingSchedule(false)
  }

  const onEditSubmit = async (values: FormValues) => {
    if (!editingSchedule) {
      return
    }
    const input = mapFormValuesToInput(values)
    await updateSchedule(editingSchedule.id, input)
    setEditingSchedule(undefined)
  }

  const onDelete = async () => {
    if (!editingSchedule) {
      return
    }

    await deleteSchedule(buildingEnergyModel, editingSchedule.id)
    setEditingSchedule(undefined)
  }

  const calcScheduleYearlyHours = (schedule: ScheduleFragment): number => {
    if (!project?.calendarYear) return 0

    return round(
      calculateHourSerieFactorsSum(schedule, project.calendarYear),
      0,
    )
  }

  return (
    <>
      <Box marginY={theme.spacing(1)}>
        <Box>
          <Box
            mb={2}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h5">
              {t('buildingEnergyModels.drawer.schedules')}
            </Typography>

            <Button
              variant="contained"
              color="primary"
              startIcon={<Plus />}
              onClick={onCreateStart}
            >
              {t('buildingEnergyModels.drawer.addSchedule')}
            </Button>
          </Box>
        </Box>
        {schedules.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width="30%">{t('schedule.name')}</TableCell>
                  <TableCell>{t('schedule.yearlyUsage')}</TableCell>
                  <TableCell>{t('schedule.additionalException')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schedules.map((schedule) => (
                  <TableRow
                    key={schedule.id}
                    hover
                    onClick={() => setEditingSchedule(schedule)}
                    style={{ cursor: 'pointer' }}
                  >
                    <TableCell>{schedule.name}</TableCell>
                    <TableCell>{`${calcScheduleYearlyHours(schedule)} ${t(
                      'schedule.yearlyUsageUnit',
                    )}`}</TableCell>
                    <TableCell>
                      {schedule.exceptionData?.inUse &&
                        schedule.exceptionData?.startAt &&
                        schedule.exceptionData.endAt &&
                        `${formatDate(
                          new Date(
                            schedule.exceptionData.startAt.replace('Z', ''),
                          ),
                          t('date.date'),
                        )} - ${formatDate(
                          new Date(
                            schedule.exceptionData.endAt.replace('Z', ''),
                          ),
                          t('date.date'),
                        )}`}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box ml={1}>
            <Typography variant="subtitle1">
              {t('schedule.noSchedules')}
            </Typography>
          </Box>
        )}
      </Box>

      {isCreatingSchedule && (
        <ScheduleDialog
          open={isCreatingSchedule}
          onClose={onDialogClose}
          onSubmit={onCreateSubmit}
          defaultValues={defaultValues}
          isCreate
        />
      )}

      {editingSchedule !== undefined && (
        <ScheduleDialog
          open={editingSchedule !== undefined}
          onClose={() => setEditingSchedule(undefined)}
          onSubmit={onEditSubmit}
          defaultValues={mapInputToFormValues(editingSchedule)}
          isCreate={false}
          onDelete={onDelete}
        />
      )}

      <LoadingDialog
        isLoading={isCreateLoading || isUpdateLoading || isDeleteLoading}
      />
    </>
  )
}
